import React, { useState, useEffect } from 'react';
import '../styles/ReasoningChain.css';

function ReasoningChain({ currentStage, reasoningSteps = [] }) {
  const [retrievalSubStage, setRetrievalSubStage] = useState(0);
  const [animatedText, setAnimatedText] = useState('');
  const [stepIndex, setStepIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [targetText, setTargetText] = useState('');

  const stages = [
    {
      id: 'analyze',
      icon: '🤔',
      title: 'Understanding your request',
      description: 'Analyzing your query and determining intent...'
    },
    {
      id: 'retrieve',
      icon: '📚',
      title: 'Retrieving context',
      description: 'Searching through knowledge sources...'
    },
    {
      id: 'generate',
      icon: '✍️',
      title: 'Generating answer',
      description: 'Constructing your response...'
    }
  ];

  // Retrieval sub-stages with dynamic text
  const retrievalSubStages = [
    { text: '📂 Pulling relevant documents...' },
    { text: '🔍 Refining context for better match...' },
    { text: '🔁 Re-ranking context based on relevance...' }
  ];

  // Find current active stage or default to first
  const activeStage = stages.findIndex(s => s.id === currentStage);

  // Update retrieval sub-stage every few seconds
  useEffect(() => {
    let interval;
    
    // Only run for retrieve stage
    if (currentStage === 'retrieve') {
      interval = setInterval(() => {
        setRetrievalSubStage((prev) => (prev + 1) % retrievalSubStages.length);
      }, 2500);
    }
    
    return () => clearInterval(interval);
  }, [currentStage]);

  // Compute target text – either animated reasoning step or default stage description
  useEffect(() => {
    let text = '';

    // If we are in analyze stage and have reasoningSteps, use them
    if (currentStage === 'analyze' && reasoningSteps.length > 0) {
      const step = reasoningSteps[stepIndex] || {};
      text = `→ ${step.thought || step.tool || ''} ${step.tool_input ? '(' + step.tool_input + ')' : ''}`.trim();
    } else {
      if (currentStage === 'retrieve') {
        text = retrievalSubStages[retrievalSubStage].text;
      } else {
        const currentStageObj = stages.find(s => s.id === currentStage);
        text = currentStageObj ? currentStageObj.description : '';
      }
    }
    setTargetText(text);
  }, [currentStage, retrievalSubStage, stepIndex, reasoningSteps]);

  // Advance to next reasoning step once current text typed out (only in analyze stage)
  useEffect(() => {
    if (currentStage !== 'analyze' || reasoningSteps.length === 0) return;

    if (charIndex === 0) {
      // reset animatedText when new step starts
      setAnimatedText('');
    }
    const step = reasoningSteps[stepIndex] || {};
    const fullText = `→ ${step.thought || step.tool || ''} ${step.tool_input ? '(' + step.tool_input + ')' : ''}`.trim();

    if (charIndex < fullText.length) {
      const timeout = setTimeout(() => {
        setAnimatedText(fullText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 30);
      return () => clearTimeout(timeout);
    }
    // Finished current step; wait briefly then move to next if exists
    if (stepIndex < reasoningSteps.length - 1) {
      const timeout = setTimeout(() => {
        setStepIndex(stepIndex + 1);
        setCharIndex(0);
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [currentStage, reasoningSteps, stepIndex, charIndex]);
  useEffect(() => {
    setAnimatedText(''); // Reset text when target changes
    
    if (!targetText) return;
    
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index < targetText.length) {
        setAnimatedText(targetText.substring(0, index + 1));
        index++;
      } else {
        clearInterval(typingInterval);
      }
    }, 20); // Speed of typing
    
    return () => clearInterval(typingInterval);
  }, [targetText]);
  
  return (
    <div className="message-container ai-message">
      <div className="processing-avatar-container">
        <div className="message-avatar ai-icon-processing"></div>
      </div>
      <div className="reasoning-chain-container">
        {stages.map((stage, index) => (
          <div 
            key={stage.id} 
            className={`reasoning-stage ${index <= activeStage ? 'active' : ''} ${index < activeStage ? 'completed' : ''}`}
          >
            <div className="stage-indicator">
              {index < activeStage ? (
                <span className="stage-icon completed-icon">✓</span>
              ) : (
                <div className={`stage-number ${index === activeStage ? 'pulse' : ''}`}>
                  <span className="stage-emoji">{stage.icon}</span>
                </div>
              )}
              <div className={`stage-line ${index < stages.length - 1 ? '' : 'hidden'}`}></div>
            </div>
            <div className="stage-content">
              <h4 className="stage-title">{stage.title}</h4>
              {index === activeStage && (
                <div className="animated-text-container">
                  <p className="stage-description typing-animation">{animatedText}</p>
                  <span className="typing-cursor">|</span>
                </div>
              )}
              {index === activeStage && (
                <div className="stage-progress">
                  <div className="dots-container">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ReasoningChain;
